// OptionsFlowProvider — owns the feed snapshot plus the three client-side
// selection dimensions (venue -> token -> window) and exposes the ACTIVE
// envelope. The feed is ONE nested `_all.json`; switching venue, token, or
// window is pure re-indexing of that snapshot — no re-request for any of them.
//
// Resolution chain, all off one snapshot:
//   rawPayload -> resolveVenueBundle -> data[venue][token] (a window-bundle)
//              -> resolveWindowBundle -> windows[window] (the 12-report envelope)

import React, { createContext, useCallback, useMemo, useRef, useState } from 'react';

import { useOptionsFlowFeed } from './use-options-flow-feed';
import { FlowFeedStatus, FlowPayload, TokenInfo, VenueComparisonReport, VenueInfo } from './types';
import {
  findToken,
  findVenue,
  resolveVenueBundle,
  resolveVenueComparison,
  resolveWindowBundle,
  WindowCoverageMap,
  WindowKey,
} from './options-flow.helpers';

export interface IOptionsFlowProvider {
  // The ACTIVE venue/token/window envelope (the 12-report shape every renderer
  // consumes). Resolved by re-indexing the one snapshot, so the page and
  // renderers stay dimension-agnostic.
  payload: FlowPayload | null;
  status: FlowFeedStatus;
  // Force an immediate flow_metrics re-fetch (the page's Refresh button), between
  // the hourly auto-polls.
  refresh: () => void;
  // Wall-clock (ms) the FE last applied a snapshot — the "refreshed HH:MM" stamp.
  lastUpdated: number | null;

  // Venue dimension (client-side). `venues` drives the selector; `activeVenue`
  // carries the capability flags cards degrade off (structures / blocks /
  // settlement hour). Switching venue resets the token to that venue's default.
  venue: string;
  setVenue: (venue: string) => void;
  venues: VenueInfo[];
  activeVenue: VenueInfo | undefined;

  // Token dimension (client-side), scoped to the active venue's token list.
  token: string;
  setToken: (token: string) => void;
  tokens: TokenInfo[];
  activeToken: TokenInfo | undefined;

  // Window dimension (client-side).
  activeWindow: WindowKey;
  setActiveWindow: (window: WindowKey) => void;
  availableWindows: WindowKey[];
  // Sparse-window coverage for the active cell (empty on dense tokens / older
  // payloads). Drives the window-selector's sparsity marker + tooltip.
  windowCoverage: WindowCoverageMap;

  // Cross-venue comparison (top-level report, spans ALL venues — independent of
  // the active venue/token/window). Null when the feed doesn't carry it.
  venueComparison: VenueComparisonReport | null;
}

const DEFAULT_WINDOW: WindowKey = '24h';

export const OptionsFlowContext = createContext<IOptionsFlowProvider>({} as IOptionsFlowProvider);

export const OptionsFlowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { payload: rawPayload, status, refresh, lastUpdated } = useOptionsFlowFeed();

  const venueBundle = useMemo(() => resolveVenueBundle(rawPayload), [rawPayload]);
  const venueComparison = useMemo(() => resolveVenueComparison(rawPayload), [rawPayload]);

  // Venue — DERIVED, not synced: follow the feed's `default_venue` until the
  // user picks; then honour their pick, clamped to a listed venue.
  const [venueState, setVenueState] = useState<string | null>(null);
  const venuePicked = useRef(false);
  // Token pick is scoped to a venue; a venue switch clears it so the token falls
  // back to the new venue's default_token.
  const tokenPicked = useRef(false);

  const setVenue = useCallback((next: string) => {
    venuePicked.current = true;
    tokenPicked.current = false;
    setVenueState(next);
  }, []);

  const venueIsListed =
    venueState != null && venueBundle.venues.some((v) => v.venue === venueState);
  const venue = venuePicked.current && venueIsListed ? venueState : venueBundle.defaultVenue;
  const activeVenue = findVenue(venueBundle.venues, venue);

  // Token — DERIVED off the active venue's token list; follow the venue's
  // `default_token` until the user picks a token that venue actually serves.
  const [tokenState, setTokenState] = useState<string | null>(null);
  const setToken = useCallback((next: string) => {
    tokenPicked.current = true;
    setTokenState(next);
  }, []);

  const tokens = activeVenue?.tokens ?? [];
  const tokenIsListed = tokenState != null && tokens.some((t) => t.token === tokenState);
  const token =
    tokenPicked.current && tokenIsListed
      ? tokenState
      : (activeVenue?.default_token ?? tokens[0]?.token ?? 'BTC');
  const activeToken = findToken(tokens, token);

  // Window bundle for the active venue/token cell.
  const windowBundle = useMemo(
    () => resolveWindowBundle(venueBundle.data[venue]?.[token] ?? null),
    [venueBundle, venue, token],
  );

  // Window — DERIVED: follow the cell's `default_window` until the user picks;
  // clamp to an available window so we never index a missing envelope.
  const [windowState, setWindowState] = useState<WindowKey>(DEFAULT_WINDOW);
  const windowPicked = useRef(false);
  const setActiveWindow = useCallback((next: WindowKey) => {
    windowPicked.current = true;
    setWindowState(next);
  }, []);

  const fallbackWindow = windowBundle.defaultWindow ?? DEFAULT_WINDOW;
  const activeWindow: WindowKey =
    windowPicked.current && windowBundle.availableWindows.includes(windowState)
      ? windowState
      : fallbackWindow;

  const payload = windowBundle.windows[activeWindow] ?? null;

  const value = useMemo<IOptionsFlowProvider>(
    () => ({
      payload,
      status,
      refresh,
      lastUpdated,
      venue,
      setVenue,
      venues: venueBundle.venues,
      activeVenue,
      token,
      setToken,
      tokens,
      activeToken,
      activeWindow,
      setActiveWindow,
      availableWindows: windowBundle.availableWindows,
      windowCoverage: windowBundle.windowCoverage,
      venueComparison,
    }),
    [
      payload,
      status,
      refresh,
      lastUpdated,
      venue,
      setVenue,
      venueBundle.venues,
      activeVenue,
      token,
      setToken,
      tokens,
      activeToken,
      activeWindow,
      setActiveWindow,
      windowBundle.availableWindows,
      windowBundle.windowCoverage,
      venueComparison,
    ],
  );

  return <OptionsFlowContext.Provider value={value}>{children}</OptionsFlowContext.Provider>;
};
